import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Send } from 'lucide-react';
import { ModelSelector, Model } from './ModelSelector';

interface TokenUsage {
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  totalTokens?: number;
}

interface MessageInputProps {
  onSendMessage: (message: string, model: string) => void;
  disabled?: boolean;
  placeholder?: string;
  models?: Model[];
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  tokenUsage?: TokenUsage | null;
  stagedMessages?: string[];
  failedMessages?: string;
  onClearFailedMessages?: () => void;
  onFocus?: () => void;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder = "Type a message...",
  models = [],
  selectedModel = 'claude-haiku-4-5',
  onModelChange = () => { },
  tokenUsage = null,
  stagedMessages = [],
  failedMessages = '',
  onClearFailedMessages = () => { },
  onFocus = () => { },
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Set failed messages in the input when they arrive
  useEffect(() => {
    if (failedMessages) {
      setMessage(failedMessages);
      onClearFailedMessages();
    }
  }, [failedMessages, onClearFailedMessages]);

  // Calculate total tokens used and get context window size
  const getContextUsageInfo = () => {
    if (!tokenUsage || !selectedModel) return null;

    // Find the selected model info
    const modelInfo = models.find(m => m.model === selectedModel);
    if (!modelInfo || !modelInfo.contextWindow) return null;

    // Calculate total tokens (input + output, with cache reads counting as input)
    const inputTokens = (tokenUsage.inputTokens || 0) + (tokenUsage.cacheReadTokens || 0);
    const outputTokens = tokenUsage.outputTokens || 0;
    const totalUsed = inputTokens + outputTokens;

    return {
      used: totalUsed,
      limit: modelInfo.contextWindow,
      percentage: Math.round((totalUsed / modelInfo.contextWindow) * 100)
    };
  };

  const contextInfo = getContextUsageInfo();

  // Format token count (e.g., 50000 -> "50k", 500 -> "0.5k", 0 -> "0k")
  const formatTokenCount = (count: number): string => {
    if (count >= 1000) {
      return Math.round(count / 1000) + 'k';
    }
    // For numbers less than 1000, show as decimal if meaningful, otherwise just "0k"
    if (count >= 100) {
      return (count / 1000).toFixed(1) + 'k';
    }
    return Math.round(count / 1000) + 'k'; // This will be "0k" for small numbers
  };

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      // Start with single line height
      textareaRef.current.style.height = '24px';
      // Let it grow based on content
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 100; // ~5 lines
      textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px';
    }
  }, [message]);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message, selectedModel);
      setMessage('');
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="w-full rounded-md bg-background focus:outline-none focus:ring-0" data-message-input-container="true">
      <Textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyPress}
        onFocus={onFocus}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className="resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 mx-3 mt-3 mb-2 overflow-y-auto scrollbar-thin"
        style={{
          width: 'calc(100% - 24px)', // Account for horizontal margins
          padding: '0' // Ensure no padding via inline style too
        }}
      />

      {/* Controls at the bottom */}
      <div className="flex items-center justify-between px-2 pb-2 pt-1">
        <div className="flex items-center gap-3">
          {models.length > 0 && (
            <ModelSelector
              selectedModel={selectedModel}
              onModelChange={onModelChange}
              models={models}
              disabled={disabled}
            />
          )}
          {contextInfo && (
            <span className="text-xs text-muted-foreground">
              Context: {formatTokenCount(contextInfo.used)} / {formatTokenCount(contextInfo.limit)}
              {contextInfo.percentage > 80 && (
                <span className="text-yellow-600 ml-1">({contextInfo.percentage}%)</span>
              )}
            </span>
          )}
        </div>
        <Button
          onClick={handleSend}
          disabled={disabled || !message.trim()}
          size="sm"
          className="ml-auto"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};